import React from 'react';
import { Box, Stack, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useIsMobile } from 'hooks/useIsMobile';

/**
 * @component QuickFilters
 * @description Unified filter bar with search, date range, and optional additional filter
 * @param {Object} props
 * @param {string} props.searchValue - Current search value
 * @param {function} props.onSearchChange - Handler for search input change
 * @param {string} [props.searchPlaceholder='Search...'] - Placeholder text for search input
 * @param {boolean} [props.showDateRange=false] - Whether to show date range picker
 * @param {Object} [props.dateRange] - Current date range value
 * @param {function} [props.onDateRangeChange] - Handler for date range change
 * @param {string} [props.dateRangeDefault='all'] - Default date range preset ('all' | '30days')
 * @param {Object} [props.additionalFilter] - Additional filter configuration
 * @param {string} props.additionalFilter.label - Label for the filter
 * @param {string|number|null} props.additionalFilter.value - Current value
 * @param {Array<{value: string|number, label: string}>} props.additionalFilter.options - Filter options
 * @param {function} props.additionalFilter.onChange - Handler for filter change
 * @param {React.ReactNode} [props.actionButton] - Optional action button to display after date range
 * @param {React.ReactNode} [props.startButton] - Optional button to display before the search bar
 * @returns {JSX.Element}
 */
export function QuickFilters({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  showDateRange = false,
  dateRange,
  onDateRangeChange,
  dateRangeDefault = 'all',
  additionalFilter,
  actionButton,
  startButton,
}) {
  const isMobile = useIsMobile();

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Stack 
        direction={isMobile ? "column" : "row"} 
        spacing={2} 
        alignItems={isMobile ? "stretch" : "center"} 
        flexWrap="wrap"
      >
        {/* Start Button (before search) */}
        {startButton}
        
        {/* Search */}
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />,
          }}
          sx={{ 
            minWidth: isMobile ? '100%' : 250,
            width: isMobile ? '100%' : 'auto',
          }}
        />

        {/* Date Range */}
        {showDateRange && (
          <>
            <TextField
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={dateRange?.from || ''}
              onChange={(e) => {
                const fromValue = e.target.value || null;
                const newRange = {
                  ...(dateRange || {}),
                  from: fromValue,
                };
                // Keep the range if either from or to is set, otherwise null
                onDateRangeChange(newRange.from || newRange.to ? newRange : null);
              }}
              sx={{ 
                minWidth: isMobile ? '100%' : 150,
                width: isMobile ? '100%' : 'auto',
              }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={dateRange?.to || ''}
              onChange={(e) => {
                const toValue = e.target.value || null;
                const newRange = {
                  ...(dateRange || {}),
                  to: toValue,
                };
                // Keep the range if either from or to is set, otherwise null
                onDateRangeChange(newRange.from || newRange.to ? newRange : null);
              }}
              sx={{ 
                minWidth: isMobile ? '100%' : 150,
                width: isMobile ? '100%' : 'auto',
              }}
            />
            {/* Action Button (e.g., Clear Filter) */}
            {actionButton}
          </>
        )}

        {/* Additional Filter Dropdown */}
        {additionalFilter && (
          <FormControl 
            size="small" 
            sx={{ 
              minWidth: isMobile ? '100%' : 200,
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <InputLabel>{additionalFilter.label}</InputLabel>
            <Select
              value={additionalFilter.value != null ? String(additionalFilter.value) : ''}
              onChange={(e) => {
                const selectedValue = e.target.value;
                const valueToPass = selectedValue === '' ? null : selectedValue;
                additionalFilter.onChange(valueToPass);
              }}
              label={additionalFilter.label}
            >
              <MenuItem value="">All {additionalFilter.label}s</MenuItem>
              {additionalFilter.options.map((option) => (
                <MenuItem key={option.value} value={String(option.value)}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
    </Box>
  );
}

